import {
  OperationsAnalyticsSummary,
  OperationsAnalyticsFeatureSummary,
  OperationsAnalyticsSiteMetric,
  OperationsAnalyticsSiteSortBy,
  OperationsAnalyticsSiteSortOrder,
  OperationsAnalyticsSites,
  OperationsAnalyticsTrends,
} from 'interfaces/operationsAnalytics';
import {IUserInfo} from 'utils/Atoms';
import {baseAPI} from 'utils/CustomAxios';

export interface SummaryParams {
  userInfo: IUserInfo | null;
  startDate: string;
  endDate: string;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
}

interface SitesParams extends SummaryParams {
  metric: OperationsAnalyticsSiteMetric;
  keyword?: string;
  page: number;
  count: number;
  sortBy: OperationsAnalyticsSiteSortBy;
  sortOrder: OperationsAnalyticsSiteSortOrder;
}

export const getOperationsAnalyticsSummary = async ({
  userInfo,
  startDate,
  endDate,
  setIsLoading,
  setErrorModal,
}: SummaryParams): Promise<OperationsAnalyticsSummary | false> => {
  setIsLoading(true);
  try {
    const response = await baseAPI
      .getAxiosInstance()
      .get('/v1/admins/operations-analytics/summary', {
        params: {startDate, endDate},
        headers: {authorization: userInfo?.accessToken},
      });
    return response.data;
  } catch (error: any) {
    setErrorModal({
      ...error.response,
      onOff: true,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

export const getOperationsAnalyticsTrends = async ({
  userInfo,
  startDate,
  endDate,
  setIsLoading,
  setErrorModal,
}: SummaryParams): Promise<OperationsAnalyticsTrends | false> => {
  setIsLoading(true);
  try {
    const response = await baseAPI
      .getAxiosInstance()
      .get('/v1/admins/operations-analytics/trends', {
        params: {startDate, endDate},
        headers: {authorization: userInfo?.accessToken},
      });
    return response.data;
  } catch (error: any) {
    setErrorModal({
      ...error.response,
      onOff: true,
    });
    return false;
  } finally {
    setIsLoading(false);
  }
};

export const getOperationsAnalyticsSites = async ({
  userInfo,
  metric,
  startDate,
  endDate,
  keyword,
  page,
  count,
  sortBy,
  sortOrder,
  setIsLoading,
  setErrorModal,
}: SitesParams): Promise<OperationsAnalyticsSites | false> => {
  setIsLoading(true);
  try {
    const response = await baseAPI
      .getAxiosInstance()
      .get('/v1/admins/operations-analytics/sites', {
        params: {
          metric,
          startDate,
          endDate,
          keyword,
          page,
          count,
          sortBy,
          sortOrder,
        },
        headers: {authorization: userInfo?.accessToken},
      });
    return response.data;
  } catch (error: any) {
    setErrorModal({...error.response, onOff: true});
    return false;
  } finally {
    setIsLoading(false);
  }
};

export const getOperationsAnalyticsFeatureSummary = async ({
  userInfo,
  startDate,
  endDate,
  setIsLoading,
  setErrorModal,
}: SummaryParams): Promise<OperationsAnalyticsFeatureSummary | false> => {
  setIsLoading(true);
  try {
    const response = await baseAPI
      .getAxiosInstance()
      .get('/v1/admins/operations-analytics/feature-summary', {
        params: {startDate, endDate},
        headers: {authorization: userInfo?.accessToken},
      });
    return response.data;
  } catch (error: any) {
    setErrorModal({...error.response, onOff: true});
    return false;
  } finally {
    setIsLoading(false);
  }
};
